import './OracleClientConfig.scss';
import { ChangeEventHandler, useEffect, useState } from 'react';
import { RiFilter2Fill } from 'react-icons/ri';
import { OnboardingStoreType } from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { SearchField } from '@Components/SearchField/SearchField';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useTabbedNav } from '@src/utils/hooks/useNav';
import { useNotImplemented } from '@src/utils/hooks/useNotImplemented';
import { OracleOnboarding } from './OracleOnboarding';
import { useOnboardingSections } from './OracleOnboarding/useOnboardingSections';
import { useOnboardingStore } from './OracleOnboarding/useOnboardingStore';
import { StoreToggle } from './OracleOnboarding/components/StoreToggle';
import { Recap } from './Recap';
import { ClientConfigTab, ClientConfigTabType } from './oracle-client-config-constants';

const PAGE_NAVS = [
  { label: 'Recap', key: ClientConfigTab.RECAP },
  { label: 'Briefing Book / Onboarding', key: ClientConfigTab.ONBOARDING }
];

export const OracleClientConfig = () => {
  const { name, cid } = useCompanyInfo();
  const { tab, goToTab } = useTabbedNav<ClientConfigTabType>({
    baseURL: 'oracle-config',
    defaultKey: ClientConfigTab.ONBOARDING,
    navs: PAGE_NAVS.map(nav => nav.key)
  });
  const [search, setSearch] = useState('');
  const setError = useSetError();

  const { store, setStore, canOverride } = useOnboardingStore();
  const { isLoading, error } = useOnboardingSections(cid, store);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load onboarding",
        message: `Failed to load the onboarding sections: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  return (
    <Page
      className='oracle-client-config'
      title={`About ${name}`}
      pageType={PAGE_NAMES.ORACLE_CLIENT_CONFIG}
      navs={PAGE_NAVS}
      selectedNav={tab}
      onNav={key => goToTab(key as ClientConfigTabType)}
      isLoading={isLoading}
      actions={
        // Only the onboarding tab reads a store, so Recap gets no toggle.
        tab === ClientConfigTab.ONBOARDING && (
          <StoreToggle store={store} setStore={setStore} canOverride={canOverride} />
        )
      }
      subHeader={
        tab === ClientConfigTab.ONBOARDING && (
          <ClientConfigToolbar search={search} onSearchChange={e => setSearch(e.target.value)} />
        )
      }
    >
      <ClientConfigBody company={cid} selectedTab={tab} store={store} />
    </Page>
  );
};

interface ClientConfigBodyProps {
  company: string;
  selectedTab: ClientConfigTabType;
  store: OnboardingStoreType;
}

// One `useOnboardingStore` for the page: the toggle in the header, the sections below it and
// this component's loading state and error modal all describe the same store.
const ClientConfigBody = ({ company, selectedTab, store }: ClientConfigBodyProps) => {
  if (selectedTab === ClientConfigTab.RECAP) {
    return <Recap />;
  }
  return <OracleOnboarding company={company} store={store} />;
};

interface ClientConfigToolbarProps {
  search: string;
  onSearchChange: ChangeEventHandler<HTMLInputElement>;
}

// Neither control filters anything yet.
const ClientConfigToolbar = ({ search, onSearchChange }: ClientConfigToolbarProps) => {
  const notImplemented = useNotImplemented();

  return (
    <div className='client-config-toolbar'>
      <Button
        appearance={Appearance.EMPTY}
        leadingElement={<RiFilter2Fill />}
        onClick={notImplemented}
      >
        Filter
      </Button>
      <SearchField
        value={search}
        onChange={onSearchChange}
        placeholder='Search Page'
        design={Design.NEUTRAL}
        className='client-config-search'
      />
    </div>
  );
};
